import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { 
  Users, DollarSign, Percent, Copy, Link, Loader2, Search,
  Edit, Trash2, Ban, Check, TrendingUp, Wallet, CreditCard,
  ArrowUpRight, Settings, RefreshCw, Eye, ExternalLink
} from 'lucide-react';
import { HelpButton } from '@/components/common/HelpButton';
import api from '@/services/api';
import { toast } from 'sonner';

export const AdminResellersTab = () => {
  const [resellers, setResellers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedReseller, setSelectedReseller] = useState(null);
  const [globalSettings, setGlobalSettings] = useState({
    default_commission_rate: 20,
    min_cashout_amount: 50,
    payout_enabled: true
  });
  const [stats, setStats] = useState({
    total_resellers: 0,
    total_earnings: 0,
    pending_payouts: 0,
    total_referrals: 0
  });

  useEffect(() => {
    loadResellers();
    loadGlobalSettings();
    loadStats();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadResellers = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/resellers?search=${search}`);
      setResellers(response.data.resellers || []);
    } catch (error) {
      console.error('Failed to load resellers:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGlobalSettings = async () => {
    try {
      const response = await api.get('/admin/reseller-settings');
      if (response.data) {
        setGlobalSettings(response.data);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/admin/reseller-stats');
      if (response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const saveGlobalSettings = async () => {
    try {
      await api.post('/admin/reseller-settings', globalSettings);
      toast.success('Settings saved successfully');
      setShowSettingsModal(false);
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const updateResellerCommission = async (resellerId, commission) => {
    try {
      await api.patch(`/admin/resellers/${resellerId}`, { commission_rate: commission });
      toast.success('Commission rate updated');
      loadResellers();
    } catch (error) {
      toast.error('Failed to update commission');
    }
  };

  const toggleResellerStatus = async (reseller) => {
    try {
      await api.patch(`/admin/resellers/${reseller.user_id}`, { 
        is_active: !reseller.is_active 
      });
      toast.success(`Reseller ${reseller.is_active ? 'deactivated' : 'activated'}`);
      loadResellers();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const processPayouts = async () => {
    if (!window.confirm('Process all pending payouts? This will initiate Stripe transfers.')) return;
    try {
      const response = await api.post('/admin/resellers/process-payouts');
      toast.success(`Processed ${response.data.processed} payouts`);
      loadResellers();
      loadStats();
    } catch (error) {
      toast.error('Failed to process payouts');
    }
  };

  const copyReferralLink = (code) => {
    const link = `${window.location.origin}/signup?ref=${code}`;
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Resellers</p>
                <p className="text-2xl font-bold">{stats.total_resellers}</p>
              </div>
              <Users className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Total Earnings</p>
                <p className="text-2xl font-bold">${stats.total_earnings?.toFixed(2) || '0.00'}</p>
              </div>
              <DollarSign className="w-10 h-10 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Pending Payouts</p>
                <p className="text-2xl font-bold">${stats.pending_payouts?.toFixed(2) || '0.00'}</p>
              </div>
              <Wallet className="w-10 h-10 text-amber-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Total Referrals</p>
                <p className="text-2xl font-bold">{stats.total_referrals}</p>
              </div>
              <TrendingUp className="w-10 h-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Resellers Management */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Reseller Management
            </CardTitle>
            <CardDescription className="text-slate-600">
              Manage resellers, commissions, and payouts
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowSettingsModal(true)}
              className="border-slate-300"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button 
              onClick={processPayouts}
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={stats.pending_payouts <= 0}
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Process Payouts
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Search resellers..." 
                value={search} 
                onChange={(e) => setSearch(e.target.value)} 
                onKeyDown={(e) => e.key === 'Enter' && loadResellers()} 
                className="pl-10 bg-white border-slate-300 text-slate-900" 
              />
            </div>
            <Button onClick={loadResellers} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>

          {/* Resellers Table */}
          <div className="border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full min-w-[1100px]">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3 text-sm text-slate-600 font-medium">Reseller</th>
                  <th className="text-left p-3 text-sm text-slate-600 font-medium">Referral Code</th>
                  <th className="text-left p-3 text-sm text-slate-600 font-medium">Commission</th>
                  <th className="text-left p-3 text-sm text-slate-600 font-medium">Referrals</th>
                  <th className="text-left p-3 text-sm text-slate-600 font-medium">Earnings</th>
                  <th className="text-left p-3 text-sm text-slate-600 font-medium">Pending</th>
                  <th className="text-left p-3 text-sm text-slate-600 font-medium">Status</th>
                  <th className="text-right p-3 text-sm text-slate-600 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {resellers.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-slate-500">
                      {loading ? 'Loading...' : 'No resellers found'}
                    </td>
                  </tr>
                ) : resellers.map((reseller) => (
                  <tr key={reseller.user_id} className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="p-3">
                      <p className="font-medium text-slate-900">{reseller.name}</p>
                      <p className="text-sm text-slate-500">{reseller.email}</p>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <code className="px-2 py-1 bg-slate-100 rounded text-sm text-slate-700">
                          {reseller.referral_code}
                        </code>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => copyReferralLink(reseller.referral_code)}
                          className="text-slate-500 hover:text-blue-600"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                    <td className="p-3">
                      <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                        {reseller.commission_rate || globalSettings.default_commission_rate}%
                      </Badge>
                    </td>
                    <td className="p-3">
                      <span className="font-medium text-slate-900">{reseller.referral_count || 0}</span>
                      <span className="text-slate-500 text-sm ml-1">users</span>
                    </td>
                    <td className="p-3">
                      <span className="font-medium text-emerald-600">
                        ${(reseller.total_earnings || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className="font-medium text-amber-600">
                        ${(reseller.pending_payout || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="p-3">
                      {reseller.is_active !== false ? (
                        <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => { setSelectedReseller(reseller); setShowEditModal(true); }}
                          className="text-blue-600 hover:bg-blue-50"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => toggleResellerStatus(reseller)}
                          className={reseller.is_active !== false ? "text-amber-600 hover:bg-amber-50" : "text-green-600 hover:bg-green-50"}
                        >
                          {reseller.is_active !== false ? <Ban className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Global Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Reseller Program Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4">
            <div className="space-y-3">
              <Label className="text-slate-700">Default Commission Rate</Label>
              <div className="flex items-center gap-4">
                <Slider
                  value={[globalSettings.default_commission_rate]}
                  onValueChange={([val]) => setGlobalSettings({ ...globalSettings, default_commission_rate: val })}
                  max={50}
                  step={1}
                  className="flex-1"
                />
                <div className="w-16 text-center">
                  <Badge className="bg-blue-100 text-blue-700">{globalSettings.default_commission_rate}%</Badge>
                </div>
              </div>
              <p className="text-xs text-slate-500">New resellers will get this rate by default</p>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">Minimum Cashout Amount</Label>
              <div className="flex items-center gap-2">
                <span className="text-slate-500">$</span>
                <Input 
                  type="number"
                  value={globalSettings.min_cashout_amount}
                  onChange={(e) => setGlobalSettings({ ...globalSettings, min_cashout_amount: Number(e.target.value) })}
                  className="bg-white border-slate-300 text-slate-900"
                  min={10}
                />
              </div>
              <p className="text-xs text-slate-500">Resellers must accumulate this amount before requesting payout</p>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-slate-700">Enable Payouts</Label>
                <p className="text-xs text-slate-500">Allow resellers to request payouts</p>
              </div>
              <Switch
                checked={globalSettings.payout_enabled}
                onCheckedChange={(checked) => setGlobalSettings({ ...globalSettings, payout_enabled: checked })}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowSettingsModal(false)}>Cancel</Button>
              <Button onClick={saveGlobalSettings} className="bg-blue-600 hover:bg-blue-700">
                Save Settings
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Reseller Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Edit Reseller Commission</DialogTitle>
          </DialogHeader>
          {selectedReseller && (
            <div className="space-y-6 pt-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900">{selectedReseller.name}</p>
                <p className="text-sm text-slate-500">{selectedReseller.email}</p>
              </div>

              <div className="space-y-3">
                <Label className="text-slate-700">Commission Rate</Label>
                <div className="flex items-center gap-4">
                  <Slider
                    value={[selectedReseller.commission_rate || globalSettings.default_commission_rate]}
                    onValueChange={([val]) => setSelectedReseller({ ...selectedReseller, commission_rate: val })}
                    max={50}
                    step={1}
                    className="flex-1"
                  />
                  <div className="w-16 text-center">
                    <Badge className="bg-blue-100 text-blue-700">
                      {selectedReseller.commission_rate || globalSettings.default_commission_rate}%
                    </Badge>
                  </div>
                </div>
                <p className="text-xs text-slate-500">This reseller's custom commission rate</p>
              </div>

              <div className="grid grid-cols-2 gap-4 p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Total Referrals</p>
                  <p className="font-medium text-slate-900">{selectedReseller.referral_count || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total Earnings</p>
                  <p className="font-medium text-emerald-600">${(selectedReseller.total_earnings || 0).toFixed(2)}</p>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
                <Button 
                  onClick={async () => {
                    await updateResellerCommission(selectedReseller.user_id, selectedReseller.commission_rate);
                    setShowEditModal(false);
                  }} 
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminResellersTab;
